import * as React from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

import { NotificationGroup } from '../NotificationGroup/NotificationGroup';
import { GroupExpansionMap, GroupingType, Group, groupNotifications } from '../../utils/Grouping';
import { StoredApplicationMap, StoredStreamMap } from '../../../store/State';
import { StoredNotification } from '../../../model/StoredNotification';
import { GROUP_MINIMIZED_STACK_SIZE, MIN_SEARCH_STRING_LENGTH } from '../../../constants';
import { SearchResults } from '../SearchResults/SearchResults';
import { EmptyNotificationListCard } from '../Cards/Cards';
import { Box } from '@openfin/ui-library';

const DEFAULT_RENDER_COUNT = 100;
const RENDER_INCREMENT = 5;

interface Props {
  notifications: StoredNotification[];
  searchString: string;
  searchMode: boolean;
  groupBy: GroupingType;
  applications: StoredApplicationMap;
  streams: StoredStreamMap;
  id: string;
}

/**
 * Component for displaying a list of notifications.
 *
 * Notifications will be grouped according to the 'groupBy' property in the component's props.
 * The grouping is managed entirely within the component - the only input is a flat list of all notifications.
 */
export const NotificationView = ({
  notifications,
  searchMode,
  searchString,
  applications,
  streams,
  groupBy,
  id,
  ...rest
}: Props): JSX.Element => {
  const [groups, setGroups] = React.useState<Group[]>([]);

  /* Progressive Rendering Devious Mechanisms */
  const [groupExpansionMap, setGroupExpansionMap] = React.useState<GroupExpansionMap>({});
  const [visibleGroups, setVisibleGroups] = React.useState<Group[]>([]);
  const [renderCount, setRenderCount] = React.useState<number>(DEFAULT_RENDER_COUNT);
  const [renderableCount, setRenderableCount] = React.useState<number>(0);

  React.useEffect(() => {
    setGroups(groupNotifications(notifications, applications, streams, groupBy));
  }, [notifications, groupBy]);

  React.useEffect(() => {
    const totalRenderable = groups
      .map((group) => {
        return isGroupExpanded(group.key) ? group.notifications.length : GROUP_MINIMIZED_STACK_SIZE;
      })
      .reduce((total, count) => {
        return total + count;
      }, 0);
    setRenderableCount(totalRenderable);
    if (totalRenderable < DEFAULT_RENDER_COUNT && renderCount > DEFAULT_RENDER_COUNT) {
      // Reset to default if card count is below treshold
      setRenderCount(DEFAULT_RENDER_COUNT);
    } else {
      populateRenderList(renderCount);
    }
  }, [groups, groupExpansionMap]);

  React.useEffect(() => {
    populateRenderList(renderCount);
  }, [renderCount]);

  const populateRenderList = (capacity: number) => {
    const renderList: Group[] = [];
    let cap = capacity;
    for (const group of groups) {
      const cardCount = isGroupExpanded(group.key)
        ? group.notifications.length
        : GROUP_MINIMIZED_STACK_SIZE;
      if (cap >= cardCount) {
        group.renderLimit = cardCount;
        renderList.push(group);
        cap -= cardCount;
        continue;
      } else if (cap > 0) {
        group.renderLimit = cap;
        renderList.push(group);
      }
      break;
    }
    setVisibleGroups([...renderList]);
  };

  const handleExpandCollapse = (groupKey: string) => {
    setGroupExpansionMap({
      ...groupExpansionMap,
      [groupKey]: groupExpansionMap[groupKey] !== true,
    });
  };

  const isGroupExpanded = (groupKey: string) => {
    return groupExpansionMap[groupKey] === true;
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const top = event.currentTarget.scrollTop;
    const height = event.currentTarget.offsetHeight;
    const scroll = event.currentTarget.scrollHeight;
    if (top + height >= scroll && renderCount < renderableCount) {
      setRenderCount(Math.min(renderCount + RENDER_INCREMENT, renderableCount));
    }
  };

  return (
    <Container className="view" onScroll={handleScroll} id={id}>
      {searchMode && searchString.length >= MIN_SEARCH_STRING_LENGTH && <SearchResults />}
      {searchString.length < MIN_SEARCH_STRING_LENGTH &&
        (notifications.length === 0 ? (
          <EmptyNotificationListCard />
        ) : (
          <TransitionGroup component={null}>
            {visibleGroups.map((group: Group) => (
              <CSSTransition
                key={group.key}
                timeout={200}
                classNames="group-item"
                exit={groupBy === group.type}
              >
                <NotificationGroup
                  key={group.key}
                  group={group}
                  handleExpandCollapse={() => {
                    handleExpandCollapse(group.key);
                  }}
                  isExpanded={() => {
                    return isGroupExpanded(group.key);
                  }}
                  {...rest}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        ))}
    </Container>
  );
};

const Container = styled(Box)`
  flex: 1;
  user-select: none;
  padding: 0px ${({ theme }) => theme.px.large};
  align-content: center;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: overlay;
`;
